"use client"

import React, { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Table } from "../../DataTable/Table"
import { ColumnDefinition } from "../../../types/table"
import { processNodeData, findNodesByName, getNodesByParent, getAllNodesOfType } from "../../../utils/assetNodeUtils"
import type { TabComponentProps } from "../../../types/tabs"

export const ErectionTab: React.FC<TabComponentProps> = ({ selectedNode, projectData, menuAction }) => {
    // Define columns for Erection Progress Report
    const columns = useMemo<ColumnDefinition[]>(() => {
        return [
            {
                key: "Loc No",
                title: "Loc No",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Structure Name at Location",
                title: "Structure Name at Location",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Structure Name",
                title: "Structure Name",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Completion Status",
                title: "Completion Status",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Completion Status Date",
                title: "Completion Status Date",
                sortable: true,
                filterable: true,
                align: "center" as const,
                type: "string",
            },
            {
                key: "Progress",
                title: "Progress",
                sortable: true,
                filterable: true,
                align: "left" as const,
                type: "string",
            },
            {
                key: "Comments",
                title: "Comments",
                sortable: true,
                filterable: true,
                align: "left" as const,
                type: "string",
            },
        ]
    }, [])

    // Generate tower data based on selection (same logic as LocationsTab)
    const towerData = useMemo(() => {
        if (!projectData || !columns.length) return []

        // Handle tower selection
        if (selectedNode?.type === "tower") {
            const towerData = findNodesByName(projectData, selectedNode.name, "tower")
            const exactTowerData = towerData.find(
                (tower) => tower.name === selectedNode.name && tower.type === "tower"
            )
            return exactTowerData ? [processNodeData(exactTowerData, columns)] : []
        }

        // Handle section selection
        if (selectedNode?.type === "section") {
            const sectionTowers = getNodesByParent(projectData, selectedNode.name, "section", "tower")
            return sectionTowers.map((tower) => processNodeData(tower, columns))
        }

        // Handle no selection or line selection - show all towers
        if (!selectedNode || selectedNode.type === "line") {
            const allTowers = getAllNodesOfType(projectData, "tower")

            // Create a Map to store unique towers by name
            const uniqueTowersMap = new Map()
            allTowers.forEach((tower) => {
                if (!uniqueTowersMap.has(tower.name)) {
                    uniqueTowersMap.set(tower.name, tower)
                }
            })

            const uniqueTowers = Array.from(uniqueTowersMap.values())
            return uniqueTowers.map((tower) => processNodeData(tower, columns))
        }

        return []
    }, [selectedNode, projectData, columns])

    if (!columns.length) return null

    return (
        <View style={styles.container}>
            <Table
                columns={columns}
                data={towerData}
                title="Erection Progress Report"
                tableName="erection"
                pageSize={10}
                menuAction={menuAction}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
})
